import React, { useEffect, useState, useCallback, memo } from "react";
import { Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useGoogleLogin } from "@react-oauth/google";
import ReCAPTCHA from "react-google-recaptcha";
import "./Login.css";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import axios from "axios";
import Loader from "../common/Loader";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";
import { useNavigate } from "react-router-dom";

const RECAPTCHA_KEY = "6Ld0OugrAAAAANg5wUSXM5GiOzVips3QSeR_Jcf4";

const isPWA = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  !!window.navigator.standalone;

// ─── Styles ───────────────────────────────────────────────────────────────────
const MOBILE_CSS = `
  .ln-card {
    box-sizing: border-box;
    width: 92%;
    max-width: 480px;
    margin: 9rem auto 2rem;
    padding: 28px 20px;
    background: #07528b;
    border-radius: 16px;
    background-image: url("/images/blue-box-bg.svg");
    background-size: cover;
  }
  .ln-google-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: white;
    color: #444;
    border: none;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 14px;
  }
  .ln-google-btn img { width: 20px; height: 20px; }
  .ln-or { text-align: center; color: rgba(255,255,255,0.75); font-size: 14px; margin: 0 0 14px; }
  .ln-field {
    display: flex;
    align-items: center;
    border: 2px solid white;
    border-radius: 25px;
    padding: 6px 12px;
    margin-bottom: 14px;
    gap: 8px;
    min-height: 48px;
    box-sizing: border-box;
  }
  .ln-field img { width: 16px; height: 16px; flex-shrink: 0; object-fit: contain; }
  .ln-input {
    flex: 1; min-width: 0; background: transparent; border: none; outline: none;
    color: white; font-size: 16px; padding: 6px 4px;
  }
  .ln-input::placeholder { color: rgba(255,255,255,0.6); }
  .ln-eye { color: white; width: 20px; flex-shrink: 0; cursor: pointer; }
  .ln-note { font-size: 13px; font-weight: 600; color: white; margin: 4px 0 14px; text-align: center; }
  .ln-captcha { display: flex; justify-content: center; margin: 0 0 16px; overflow: hidden; }
  @media (max-width: 360px) { .ln-captcha > div { transform: scale(0.88); transform-origin: center; } }
  @media (max-width: 300px) { .ln-captcha > div { transform: scale(0.72); transform-origin: center; } }
  .ln-submit {
    width: 100%; padding: 13px; background: white; color: #07528B;
    font-size: 17px; font-weight: 700; border: none; border-radius: 30px;
    cursor: pointer; margin-bottom: 16px; letter-spacing: 0.3px; transition: opacity 0.15s;
  }
  .ln-submit:active { opacity: 0.8; }
  .ln-bottom { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
  .ln-remember { display: flex; align-items: center; gap: 6px; color: white; font-size: 14px; font-weight: 600; }
  .ln-forgot { color: white; text-decoration: underline; font-size: 14px; font-weight: 600; }

  /* ── TOTP Challenge overlay ── */
  .totp-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 999999;
  }
  .totp-card {
    background: #07528b; border: 2px solid #e97730;
    border-radius: 16px; padding: 32px 28px;
    width: 90%; max-width: 380px; text-align: center;
    background-image: url("/images/blue-box-bg.svg");
    background-size: cover;
  }
  .totp-title { color: white; font-size: 20px; font-weight: 700; margin-bottom: 8px; }
  .totp-sub   { color: rgba(255,255,255,0.75); font-size: 13px; margin-bottom: 24px; line-height: 1.5; }
  .totp-input {
    width: 100%; background: rgba(255,255,255,0.1);
    border: 2px solid rgba(255,255,255,0.5); border-radius: 12px;
    padding: 14px; font-size: 28px; font-weight: 800;
    color: white; text-align: center; letter-spacing: 0.4em;
    outline: none; margin-bottom: 16px; box-sizing: border-box;
  }
  .totp-input:focus { border-color: #e97730; }
  .totp-input::placeholder { color: rgba(255,255,255,0.3); letter-spacing: 0.2em; font-size: 20px; }
  .totp-btn {
    width: 100%; padding: 13px; background: #e97730;
    color: white; font-size: 16px; font-weight: 700;
    border: none; border-radius: 30px; cursor: pointer; margin-bottom: 12px;
  }
  .totp-cancel { color: rgba(255,255,255,0.6); font-size: 13px; cursor: pointer; text-decoration: underline; }
  .totp-timer { color: rgba(255,255,255,0.5); font-size: 12px; margin-top: 10px; }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────
const EyeIcon = memo(({ show, onToggle }) =>
  show
    ? <FaEyeSlash onClick={onToggle} style={{ color:"white", width:"20px", flexShrink:0, cursor:"pointer" }} />
    : <FaEye     onClick={onToggle} style={{ color:"white", width:"20px", flexShrink:0, cursor:"pointer" }} />
);

const GoogleButtonWeb = memo(({ onClick }) => (
  <button onClick={onClick} style={{ marginLeft:"65px", justifyContent:"center", display:"flex", alignItems:"center", backgroundColor:"white", color:"black", border:"none", borderRadius:"4px", padding:"10px 20px", fontSize:"18px", cursor:"pointer" }}>
    <img src="/images/google_logo_icon.svg" alt="Google logo" className="google-logo" />
    Sign Up with Google
  </button>
));

// ─── TOTP Challenge Modal ─────────────────────────────────────────────────────
const TotpChallenge = memo(({ onVerify, onCancel, loading }) => {
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(30 - (Math.floor(Date.now() / 1000) % 30));

  // countdown timer for 30-sec TOTP window
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(30 - (Math.floor(Date.now() / 1000) % 30));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    if (val.length === 6) onVerify(val);   // auto-submit on 6 digits
  };

  return (
    <div className="totp-overlay">
      <div className="totp-card">
        <div className="totp-title">🔐 Two-Factor Authentication</div>
        <div className="totp-sub">
          Enter the 6-digit code from your<br />
          <strong>Google Authenticator</strong> app
        </div>
        <input
          className="totp-input"
          type="text"
          inputMode="numeric"
          placeholder="000000"
          value={code}
          onChange={handleChange}
          autoFocus
          maxLength={6}
        />
        <button
          className="totp-btn"
          onClick={() => onVerify(code)}
          disabled={code.length !== 6 || loading}
        >
          {loading ? "Verifying…" : "Verify & Sign In"}
        </button>
        <div>
          <span className="totp-cancel" onClick={onCancel}>
            ← Use a different account
          </span>
        </div>
        <div className="totp-timer">Code refreshes in {timeLeft}s</div>
      </div>
    </div>
  );
});

// ─── Main Login Component ─────────────────────────────────────────────────────
const Login = ({ isMobile, ip }) => {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [showPass,        setShowPass]        = useState(false);
  const [email,           setEmail]           = useState("");
  const [password,        setPassword]        = useState("");
  const [googleUser,      setGoogleUser]      = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [isVerified,      setIsVerified]      = useState(false);

  // ── TOTP state ──────────────────────────────────────────────────────────────
  const [showTotpChallenge, setShowTotpChallenge] = useState(false);
  const [pendingUserId,     setPendingUserId]     = useState(null);
  const [totpLoading,       setTotpLoading]       = useState(false);

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />), [isMobile]);

  // ── Google SSO ───────────────────────────────────────────────────────────────
  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => setGoogleUser(codeResponse),
    onError:   (error) => console.log("Login Failed:", error),
  });

  useEffect(() => {
    if (!googleUser) return;
    axios.get(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${googleUser.access_token}`, {
      headers: { Authorization:`Bearer ${googleUser.access_token}`, Accept:"application/json" },
    })
    .then((res) => SocialRegisterCall({ name:res.data.name, email:res.data.email, id:res.data.id }))
    .catch((err) => console.log(err));
  }, [googleUser]);

  const SocialRegisterCall = useCallback(async (data) => {
    if (!data) return;
    setLoading(true);
    const login_With = isPWA() ? "pwa" : "web";
    const payload = { name:data.name, phone:"", ip, email:data.email, password:"", password_confirmation:"", login_by:"google", id:data.id, device_token:"", login_with:login_With };
    try {
      const enc = await apiEncryptRequest(payload);
      const res = await instance.post("/register", enc);
      const loginResponse = await decryptData(res.data);
      if (loginResponse[0].success) {
        login(loginResponse[0].user);
      } else {
        showToast("Oops! Something went wrong, Please try again");
      }
    } catch {
      showToast("Oops! Something went wrong. Please try again.");
    }
    setLoading(false);
  }, [ip, login, showToast]);

  // ── Email OTP flow (existing, untouched) ─────────────────────────────────────
  const GetOTP = useCallback(async (userID) => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: userID, email, otp: null });
      const res  = await instance.post("/getotp", enc);
      const data = await decryptData(res.data);
      toast((t) => <AppToast id={t.id} message={data.message} />);
      if (data.status) {
        setTimeout(() => navigate("/otpverify-2fa", { state:{ email, userId: userID } }), 500);
      }
    } catch (error) {
      console.error("Error during OTP fetch:", error);
      toast((t) => <AppToast id={t.id} message={String(error)} />);
    }
    setLoading(false);
  }, [email, navigate]);

  // ── TOTP verify (called from TotpChallenge modal) ────────────────────────────
  const handleTotpVerify = useCallback(async (code) => {
    if (!code || code.length !== 6) return;
    setTotpLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: pendingUserId, code });
      const res  = await instance.post("/2fa/verify-login", enc);
      const data = await decryptData(res.data);

      if (data.success) {
        setShowTotpChallenge(false);
        login(data.user);
        showToast(`Welcome back! Logged in successfully.`);
      } else {
        showToast(data.message || "Invalid code. Try again.");
      }
    } catch {
      showToast("Verification failed. Please try again.");
    }
    setTotpLoading(false);
  }, [pendingUserId, login, showToast]);

  const handleTotpCancel = useCallback(() => {
    setShowTotpChallenge(false);
    setPendingUserId(null);
  }, []);

  // ── Main login ───────────────────────────────────────────────────────────────
  const LoginCall = useCallback(async () => {
    if (!isVerified)  { showToast("Validate Captcha");      return; }
    if (!email)       { showToast("Please Enter Email");    return; }
    if (!password)    { showToast("Please Enter Password"); return; }

    setLoading(true);
    const login_With = isPWA() ? "pwa" : "web";
    const payload = { ip, email, password, login_by:"manual", login_with:login_With, device_token:"" };

    try {
      const enc = await apiEncryptRequest(payload);
      const res = await instance.post("/login", enc);
      const loginResponse = await decryptData(res.data);

      if (loginResponse.success === false) {
        showToast(loginResponse.message);
        setLoading(false);
        return;
      }

      const userDetails = loginResponse[0]?.user;
      const success     = loginResponse[0]?.success;
      const message     = loginResponse[0]?.message;

      if (success) {
        // ── Check TOTP first (Google Authenticator) ──────────────────────────
        if (userDetails?.totp_enabled === 1) {
          setPendingUserId(userDetails.id);
          setShowTotpChallenge(true);
          setLoading(false);
          return;
        }

        // ── Fallback: existing email OTP flow ────────────────────────────────
        if (userDetails?.["2fa_status"] === 1) {
          await GetOTP(userDetails.id);
          return;
        }

        // ── No 2FA — direct login ────────────────────────────────────────────
        login(userDetails);
        showToast(`${message}\n ip: ${loginResponse[0].ip}`);

      } else {
        showToast(message);
      }
    } catch (error) {
      console.error("Login error:", error);
      showToast("Oops! Something went wrong. Please try again.");
    }
    setLoading(false);
  }, [isVerified, email, password, ip, login, showToast, GetOTP]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{MOBILE_CSS}</style>

      {/* TOTP Challenge Modal */}
      {showTotpChallenge && (
        <TotpChallenge
          onVerify={handleTotpVerify}
          onCancel={handleTotpCancel}
          loading={totpLoading}
        />
      )}

      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div className="ln-card">
          <button className="ln-google-btn" onClick={loginWithGoogle}>
            <img src="/images/google_logo_icon.svg" alt="Google logo" />
            Sign in with Google
          </button>
          <div className="ln-or">or</div>
          <div className="ln-field">
            <img src="/images/email.svg" alt="" />
            <input className="ln-input" type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="ln-field">
            <img src="/images/lock.svg" alt="" />
            <input className="ln-input" type={showPass ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <EyeIcon show={showPass} onToggle={() => setShowPass((p) => !p)} />
          </div>
          <p className="ln-note">Note: All fields are mandatory</p>
          <div className="ln-captcha">
            <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={() => setIsVerified(true)} />
          </div>
          <button className="ln-submit" onClick={LoginCall}>Login</button>
          <div className="ln-bottom">
            <label className="ln-remember">
              <input type="checkbox" />
              Remember me
            </label>
            <a href="/forgotpassword" className="ln-forgot">Forgot password?</a>
          </div>
        </div>
      ) : (
        /* ── WEB (unchanged) ─────────────────────────────────────────────── */
        <div style={{ minHeight:"55rem" }}>
          <Row>
            <div className="col-md-4" />
            <div className="col-md-4">
              <div className="register-main see-full" style={{ marginTop:"8rem" }}>
                <div className="bg-img"><img src="/images/blue-box-bg.svg" alt="" /></div>
                <form className="see-full">
                  <input type="hidden" name="_token" value="IHks1cEdGGmsvouWsdVeWVHE29KFoaLV0iN8cPkE" />
                  <div className="btm mob">
                    <div className="google-act">
                      <a><img src="/images/google_logo_icon.svg" alt="" /><span>Sign Up with Google</span></a>
                    </div>
                    <div className="or">or</div>
                  </div>
                  <div className="form-top">
                    <div className="input-container">
                      <img src="/images/email.svg" alt="" className="imgIcon-signup" />
                      <input value={email} type="text" name="email" className="input-signup" placeholder="Email Address" onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div className="input-container" style={{ marginTop:"15px" }}>
                      <img src="/images/lock.svg" alt="" className="imgIcon-signup" />
                      <input type={showPass ? "text" : "password"} id="password-field" className="input-signup" name="password" placeholder="Password" onChange={(e) => setPassword(e.target.value)} />
                      <EyeIcon show={showPass} onToggle={() => setShowPass((p) => !p)} />
                    </div>
                    <span style={{ fontSize:"24px" }}>Note: All fields are mandatory</span>
                    <div style={{ marginTop:"20px", display:"block", marginBottom:"40px", textAlign:"center", marginLeft:"8rem" }}>
                      <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={() => setIsVerified(true)} />
                    </div>
                    <div className="check-btm">
                      <div className="click">
                        <div className="checkbox-container">
                          <input type="checkbox" style={{ fontSize:"24px" }} />
                          <label htmlFor="rememberMe" style={{ fontSize:"24px" }}>Remember me</label>
                        </div>
                        <a href="/forgotpassword" style={{ fontSize:"24px" }}>Forgot password?</a>
                      </div>
                    </div>
                  </div>
                  <div className="log-in" style={{ display:"grid", justifyItems:"center" }} onClick={LoginCall}>
                    <a className="media-link">
                      <div className="media-banner" style={{ width:"auto", height:"50px", marginTop:"10px" }}>
                        <img className="normal-banner"    src="/images/signup-btn-bg.png" alt="" />
                        <img className="hover-img-banner" src="/images/search-btn-hover.png" alt="" />
                        <span className="login-text" style={{ fontSize:"23px", color:"#07528B", marginTop:"0px" }}>Login</span>
                      </div>
                    </a>
                  </div>
                </form>
                <div className="btm desk" style={{ alignItems:"center" }}>
                  <div className="or" style={{ fontSize:"24px", paddingRight:"20px" }}>or</div>
                  <GoogleButtonWeb onClick={loginWithGoogle} />
                  <span className="rgst mob" style={{ fontSize:"24px" }}>Don't you have an account?</span>
                  <a href="/signUp" style={{ fontSize:"24px" }}>Sign Up</a>
                </div>
              </div>
            </div>
            <div className="col-md-4" />
          </Row>
        </div>
      )}

      {loading && (
        <div className="loading-overlay" style={{ zIndex:"9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default Login;